import moment from 'moment';

import { IOrderLine, IReturnPackagingSettings } from '@cakeiteasy/web-utils';

import { ICart } from '../../cart/models/cart/i-cart';
import { ICartLine } from '../../cart/models/cart/i-cart-line';
import { CART_LINE_TYPES, UI_DELIVERY_OPTIONS } from '../../cart/constants/common';
import { RETURN_METHODS } from '../models/return-packaging.constants';
import { IReturnPickupLocation } from '../models/i-return-pickup-location';
import { IReturnPackagingLine, IReturnPackagingOrderLine } from '../models/i-return-packaging-details';
import { IReturnScheduleDelivery } from '../models/i-return-schedule-delivery';

export function getReturnSettings(cart: ICart): IReturnPackagingSettings {
  return (cart && cart.bakery && cart.bakery.returnPackagingSettings) || null;
}

export function getReturnPackagingOrderLine(orderLines: IOrderLine[]): IReturnPackagingOrderLine | null {
  const line = (orderLines || [])
    .find(({ type }) => type === CART_LINE_TYPES.RETURN_PACKAGING) as IReturnPackagingOrderLine;

  return line?.returnPackaging ? line : null;
}

export function cartRequiresReturn(lines: ICartLine[]): boolean {
  return (lines || []).some((line) =>
    Boolean(line.product && line.product.type && line.product.type.requiresReturnablePackaging),
  );
}

const KNOWN_RETURN_METHODS: string[] = Object.values(RETURN_METHODS);

export function getAvailableReturnMethods(settings: IReturnPackagingSettings): RETURN_METHODS[] {
  return ((settings?.availableReturnMethods || [])
    .filter((method) => KNOWN_RETURN_METHODS.includes(method))) as RETURN_METHODS[];
}

export function isReturnStepRequired(cart: ICart): boolean {
  return cart && cartRequiresReturn(cart.lines) && getAvailableReturnMethods(getReturnSettings(cart)).length > 0;
}

/**A saved return the cart has no reason to carry any more: the last product needing returnable
 * packaging is gone. Distinct from an outdated line — that one stopped fitting the delivery, this
 * one stopped having anything to return. The line hangs off the cart rather than off the product,
 * so removing the product never takes it with it.*/
export function isReturnLineOrphaned(cart: ICart): boolean {
  return Boolean(cart && cart.returnPackaging) && !cartRequiresReturn(cart.lines);
}

/**A pickup at an outlet also carries a post number — the outlet's — so the type is what separates a
 * location the customer is actually at from one they only collected from.*/
function isDeliveryToDoor(delivery: IReturnScheduleDelivery): boolean {
  return Boolean(delivery) && delivery.type === UI_DELIVERY_OPTIONS.DELIVERY && Boolean(delivery.postNumber);
}

/**Where the courier should collect from, or null when the cart cannot answer that and the customer
 * has to. Only a delivery to door leaves a location behind worth reusing: after a pickup, and for
 * gift deliveries — which report type DELIVERY but keep the address under `deliveryPostNumber` —
 * there is no address of the customer's to reuse.*/
export function getReturnPickupLocation(cart: ICart): IReturnPickupLocation | null {
  const delivery: IReturnScheduleDelivery = cart && cart.delivery;

  if (!isDeliveryToDoor(delivery)) {
    return null;
  }

  return {
    postNumber: delivery.postNumber,
    address: delivery.address || '',
    city: delivery.cityName || '',
  };
}

/**Returns are always scheduled strictly after the delivery, so the calendar opens the next day.
 * Null when the delivery carries no date — mail and gift deliveries never send one — and callers
 * must read that as "no constraint".*/
export function getEarliestReturnDate(delivery: IReturnScheduleDelivery): Date | null {
  if (!delivery || !delivery.date) {
    return null;
  }

  return moment(delivery.date).add(1, 'day').startOf('day').toDate();
}

/**True when the saved return no longer matches the cart it was saved against: the delivery moved
 * past it, the seller stopped offering that method, or the courier price was quoted for a post code
 * the customer has since changed. Such a line is dropped rather than shown.*/
export function isReturnLineOutdated(
  line: IReturnPackagingLine,
  delivery: IReturnScheduleDelivery,
  settings: IReturnPackagingSettings,
): boolean {
  const details = line && line.returnPackaging;

  if (!details) {
    return true;
  }

  const { returnMethod, scheduledDate, pickupPostNumber } = details;
  const earliest = getEarliestReturnDate(delivery);

  const isMethodUnavailable = !getAvailableReturnMethods(settings).includes(returnMethod);
  const isScheduledBeforeDelivery = Boolean(earliest)
    && moment(scheduledDate, 'YYYY-MM-DD').isBefore(earliest, 'day');
  /**Only comparable when the delivery is the one the pickup was seeded from — anything else never
   * dictated the post code, so a mismatch says nothing about the saved line.*/
  const isPostNumberOutdated = returnMethod === RETURN_METHODS.COURIER
    && isDeliveryToDoor(delivery)
    && pickupPostNumber !== delivery.postNumber;

  return isMethodUnavailable || isScheduledBeforeDelivery || isPostNumberOutdated;
}
